import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, InjectionToken, OnInit, computed, inject, signal } from '@angular/core';
import { addIcons } from 'ionicons';
import { cloudDownloadOutline, cloudUploadOutline, openOutline } from 'ionicons/icons';

import { syncSummaryOf } from '@shared/components/layout/app-header/sync-status/sync-status.view';
import { OutboxService } from '@shared/services/backend/outbox/outbox.service';
import { PendingWritesService } from '@shared/services/backend/pending-writes.service';
import { ConnectivityService } from '@shared/services/connectivity.service';
import { KzLocaleService } from '@shared/services/locale/kz-locale.service';
import { KzDesktopInstaller } from '../min-app-build';
import { AppBuildGateService } from '../app-build-gate.service';
import { inertSiblings } from './inert-siblings';

/** The Koomzo listing on Google Play (one listing for every env: `com.koomzo.apps`). */
export const KZ_PLAY_LISTING_URL = 'https://play.google.com/store/apps/details?id=com.koomzo.apps';

/**
 * Opens a store listing or an installer OUTSIDE the app — a spec provides its own. `window.open(url, '_blank')`: the
 * Android WebView hands an external https link to the system (Play opens its listing), and the Electron shell's window
 * policy denies the window and opens the link in the browser (`electron/customer-display-window.ts` `denyAll`).
 */
export const UPDATE_OPEN_EXTERNAL = new InjectionToken<(url: string) => void>('UPDATE_OPEN_EXTERNAL', {
  providedIn: 'root',
  factory: () => (url: string) => void window.open(url, '_blank', 'noopener'),
});

/**
 * kz-update-required — the "Update required" screen of the minimum-version gate (kzd-ac AC-R6), over the whole app,
 * the sign-in page included, while `AppBuildGateService.blocked()`.
 *
 * Built like the app's other blocking screens (the locked kiosk, the auth frame): a full-screen layer on the theme's
 * ground, the brand lockup, then kz-ui's `kz-empty-state` in its warning tone with the one action, and a
 * `kz-inline-alert` for what is still sending. No dismissal: the only way past it is a newer build.
 *
 * - Android: **Open Google Play** (the Koomzo listing).
 * - iOS: says to update from the App Store (no listing yet — ruling AC-R5: iOS when it ships).
 * - Desktop: **Download koomzo {version}** when the releases manifest lists an installer for this computer at or above
 *   the minimum (there is no in-app updater); otherwise it says to get the new version.
 *
 * It stops new work only. Underneath, the Firestore queue, the outbox and the offline sync keep sending; while anything
 * is still waiting it says how much ("3 changes are still sending — keep the app open and online"), counted as the
 * header's sync chip counts it (`syncSummaryOf`).
 *
 * Everything else in `ion-app` is `inert` while it is up (review MV-4, `inertSiblings`) — the pages and any Ionic overlay,
 * one opened before the gate rose (a modal, a PIN pad) or after it: none keeps keyboard focus under the layer, and none
 * is dismissed (a sale in progress is still there after the update). Focus moves to the layer.
 *
 * The words are read in TypeScript (`KzLocaleService.translate`), like the header's chip.
 */
@Component({
  selector: 'app-kz-update-required',
  templateUrl: './kz-update-required.component.html',
  styleUrls: ['./kz-update-required.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class KzUpdateRequiredComponent implements OnInit, AfterViewInit {
  private readonly gate = inject(AppBuildGateService);
  private readonly locale = inject(KzLocaleService);
  private readonly net = inject(ConnectivityService);
  private readonly outbox = inject(OutboxService);
  private readonly writes = inject(PendingWritesService);
  private readonly openExternal = inject(UPDATE_OPEN_EXTERNAL);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  readonly platform = this.gate.platform;
  /** Electron: the installer the releases manifest offers (`null` until read, offline, or none fits). */
  readonly installer = signal<KzDesktopInstaller | null>(null);

  constructor() {
    /* Shown offline too (a register that saw the gate once stays gated): the icons ship in the code. */
    addIcons({
      'cloud-download-outline': cloudDownloadOutline,
      'cloud-upload-outline': cloudUploadOutline,
      'open-outline': openOutline,
    });
  }

  ngOnInit(): void {
    if (this.platform === 'electron') {
      void this.gate.desktopInstaller().then((i) => this.installer.set(i));
    }
  }

  /** The rest of the app root goes inert (MV-4) until this layer goes; focus leaves whatever had it, for the layer. */
  ngAfterViewInit(): void {
    const host = this.host.nativeElement;
    this.destroyRef.onDestroy(inertSiblings(host));
    host.querySelector<HTMLElement>('.kz-upd')?.focus();
  }

  readonly title = computed(() => {
    this.locale.lang();
    return this.t('app.updateRequired.title');
  });

  readonly message = computed(() => {
    this.locale.lang();
    switch (this.platform) {
      case 'android':
        return this.t('app.updateRequired.android');
      case 'ios':
        return this.t('app.updateRequired.ios');
      default:
        return this.installer() ? this.t('app.updateRequired.desktop') : this.t('app.updateRequired.desktopNoInstaller');
    }
  });

  /** The one action: Play on Android, the installer on a desktop that has one; none otherwise. */
  readonly actionLabel = computed<string | undefined>(() => {
    this.locale.lang();
    if (this.platform === 'android') {
      return this.t('app.updateRequired.openPlay');
    }
    const i = this.installer();
    return this.platform === 'electron' && i ? this.t('app.updateRequired.download', { version: i.version }) : undefined;
  });

  /** What this device still has to send: the header chip's count (the person's, and others' in offline mode). */
  private readonly sync = computed(() =>
    syncSummaryOf(this.net.online(), this.outbox.listed(), {
      pending: this.writes.pending(),
      earlier: this.writes.earlier(),
      rejected: this.writes.rejected(),
      offline: this.writes.offline(),
    }),
  );
  readonly unsent = computed(() => this.sync().waiting + this.sync().othersWaiting);

  /** "3 changes are still sending — keep the app open and online", or '' when nothing waits. */
  readonly unsentText = computed(() => {
    this.locale.lang();
    const n = this.unsent();
    if (n === 1) return this.t('app.updateRequired.unsentOne');
    if (n > 1) return this.t('app.updateRequired.unsentOther', { n });
    return this.sync().earlier ? this.t('app.updateRequired.unsentEarlier') : '';
  });

  /** "This version: 950 · Needed: 990" — what support asks first. */
  readonly versionsText = computed(() => {
    this.locale.lang();
    const unknown = this.t('app.updateRequired.unknown');
    return this.t('app.updateRequired.versions', { have: this.gate.thisBuild() ?? unknown, need: this.gate.required() ?? unknown });
  });

  act(): void {
    if (this.platform === 'android') {
      this.openExternal(KZ_PLAY_LISTING_URL);
      return;
    }
    const i = this.installer();
    if (this.platform === 'electron' && i) {
      this.openExternal(i.url);
    }
  }

  private t(key: string, params?: Readonly<Record<string, string | number>>): string {
    return this.locale.translate(key, params);
  }
}
